import { type CursorPage, formatDateTime, ListView, mapRows, PageHeader, useCursorList } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ConfirmMorph } from "@majitap/ui/arc/confirm-morph/confirm-morph";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { type InfiniteData, type QueryClient, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { developerActions, ownerPage, type StaffApiToken, TOKEN_STATUS } from "../../developers";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import styles from "./settings.module.css";

export const tokenFilters = filterSearch({
  status: ["active", "revoked", "all"] as const,
  // one business's or partner's tokens, from their page
  business_id: "text",
  partner_id: "text",
});

/** A revoked token shows as revoked in every loaded list at once (the API's answer, owner kept). */
export function showRevoked(queryClient: QueryClient, revoked: Omit<StaffApiToken, "owner">) {
  queryClient.setQueriesData<InfiniteData<CursorPage<StaffApiToken>>>({ queryKey: ["api-tokens"] }, (data) =>
    mapRows(data, (rows) => rows.map((t) => (t.id === revoked.id ? { ...t, ...revoked } : t))),
  );
}

/**
 * API tokens (P8-25): every business's and partner's tokens, and Revoke. Staff can't create one:
 * owners make their own in the portal or the partner app.
 */
export function ApiTokensPage(): ReactNode {
  const search = useSearch({ from: "/console/settings/api-tokens" });
  const navigate = useNavigate({ from: "/settings/api-tokens" });
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const status = search.status ?? "active";
  const list = useCursorList({
    queryKey: ["api-tokens", status, search.business_id, search.partner_id],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/api-tokens", {
          params: {
            query: {
              status: status === "all" ? undefined : status,
              business_id: search.business_id,
              partner_id: search.partner_id,
              cursor,
              limit: 25,
            },
          },
        }),
      ),
  });
  return (
    <>
      <PageHeader
        title="API tokens"
        description="Tokens businesses and partners use for the API. They make their own. Revoking one stops their system at once."
      />
      {search.business_id || search.partner_id ? (
        <p className={styles.muted}>
          Only {list.rows[0]?.owner?.name ?? "their"}'s tokens.{" "}
          <Link to="/settings/api-tokens" search={{}} className={styles.link}>
            Show everyone's
          </Link>
        </p>
      ) : null}
      <ListView
        caption="API tokens"
        itemName={{ one: "token", other: "tokens" }}
        rows={list.rows}
        rowKey={(t) => t.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={false}
        empty={{ title: "No tokens", description: "Businesses and partners create tokens for their own systems." }}
        toolbar={
          <SegmentedControl
            label="Status"
            value={status}
            onValueChange={(v) =>
              navigate({ search: (s) => ({ ...s, status: v === "active" ? undefined : (v as "revoked" | "all") }) })
            }
            options={[
              { value: "active", label: "Active" },
              { value: "revoked", label: "Revoked" },
              { value: "all", label: "All" },
            ]}
          />
        }
        columns={[
          {
            key: "name",
            label: "Token",
            render: (t) => (
              <span className={styles.stack}>
                <span>{t.name}</span>
                <span className={styles.code}>{t.prefix}…</span>
              </span>
            ),
          },
          {
            key: "owner",
            label: "Belongs to",
            render: (t) => {
              const href = ownerPage(t.owner);
              return t.owner && href ? (
                <span className={styles.stack}>
                  <RouterLink href={href} className={styles.link}>
                    {t.owner.name}
                  </RouterLink>
                  <span className={styles.muted}>{t.owner.type === "business" ? "Business" : "Partner"}</span>
                </span>
              ) : (
                "—"
              );
            },
          },
          {
            key: "scopes",
            label: "Can",
            render: (t) => <span className={styles.muted}>{t.scopes.join(", ")}</span>,
          },
          {
            key: "limits",
            label: "Limits",
            render: (t) => (
              <span className={styles.stack}>
                <span className={styles.muted}>{t.rate_limit_per_min} calls a minute</span>
                <span className={styles.muted}>
                  {t.expires_at ? `Expires ${formatDateTime(t.expires_at, format)}` : "No expiry"}
                </span>
              </span>
            ),
          },
          {
            key: "created_by",
            label: "Made by",
            render: (t) => (
              <span className={styles.muted}>
                {[t.created_by?.name, formatDateTime(t.created_at, format)].filter(Boolean).join(" · ")}
              </span>
            ),
          },
          {
            key: "last_used_at",
            label: "Last used",
            render: (t) => (
              <span className={styles.muted}>{t.last_used_at ? formatDateTime(t.last_used_at, format) : "Never"}</span>
            ),
          },
          {
            key: "status",
            label: "Status",
            render: (t) => {
              const [label, tone] = TOKEN_STATUS[t.status];
              return (
                <span className={styles.stack}>
                  <Badge tone={tone}>{label}</Badge>
                  {t.revoked_at ? (
                    <span className={styles.muted}>
                      {t.revoke_reason === "admin" ? "By MajiTap staff" : "By its owner"},{" "}
                      {formatDateTime(t.revoked_at, format)}
                    </span>
                  ) : null}
                </span>
              );
            },
          },
          {
            key: "actions",
            label: "",
            render: (t) =>
              t.status === "active" ? (
                <span className={styles.morphCell}>
                  <ConfirmMorph
                    label="Revoke"
                    prompt="Revoke it?"
                    confirmLabel="Revoke"
                    pendingLabel="Revoking"
                    doneLabel="Revoked"
                    onConfirm={async () => showRevoked(queryClient, await developerActions.revokeToken(t.id))}
                    resultTimeout={1500}
                  />
                </span>
              ) : null,
          },
        ]}
      />
    </>
  );
}
